import type { MeetingRecapState, MeetingState } from "#shared/meetingsContract.ts";

export const en = {
    meetingStates: {
        scheduled: "Scheduled",
        draft: "To review",
        ready: "Agenda opened",
        completed: "Completed"
    } satisfies Record<Lowercase<MeetingState>, string>,
    meetingTemplates: {
        default: "Simple check-in",
        compensation: "Compensation and salary",
        departure: "Departure and offboarding",
        difficult_conversation: "Difficult conversation",
        goal_setting: "Goal-setting and planning",
        growth_discussion: "Growth discussion",
        new_hire: "New hire",
        performance_conversation: "Performance conversation",
        promotion: "Promotion",
        return_from_leave: "Return from extended leave",
        skip_level: "Skip-level",
        team_alignment: "Team alignment"
    },
    MeetingsTable: {
        ariaLabel: "Meetings",
        untitledMeeting: "Untitled meeting",
        columns: {
            meeting: "Meeting",
            when: "When",
            duration: "Duration",
            status: "Status",
            actions: "Actions"
        },
        meetingTypes: {
            OneOnOne: "1:1",
            Team: "Team"
        },
        recap: {
            present: "Recap",
            absent: "No recap"
        }
    },
    MeetingRowActions: {
        ariaLabel: "Actions for {{title}}",
        untitledMeeting: "Untitled meeting",
        openInCalendar: "Open in calendar",
        opensInNewTab: "Opens in a new tab",
        delete: "Delete",
        cancel: "Cancel",
        deleteDialog: {
            title: "Delete this agenda?",
            description: "The talking points, private notes and recap for “{{title}}” will be deleted. This can’t be undone. The calendar event stays, and any action items created from this meeting are kept."
        },
        toast: {
            deleted: "Agenda deleted",
            deleteError: "The agenda couldn’t be deleted. Try again."
        }
    },
    MeetingsToolbar: {
        searchPlaceholder: "Search meetings and what was discussed",
        searchAriaLabel: "Search meetings and what was discussed",
        state: {
            all: "All statuses",
            ariaLabel: "Filter by status",
            clearAriaLabel: "Clear the status filter"
        },
        conversationType: {
            all: "All conversation types",
            ariaLabel: "Filter by conversation type",
            clearAriaLabel: "Clear the conversation-type filter"
        },
        when: {
            all: "All meetings",
            upcoming: "Upcoming",
            last30Days: "Last 30 days",
            customRange: "Custom range",
            ariaLabel: "Filter by date",
            clearAriaLabel: "Clear the date filter",
            rangeLabel: "Custom date range",
            clearTimeRange: "Clear time range"
        },
        resetFilters: "Reset filters"
    },
    MeetingsGapBanner: {
        message_one: "{{count}} of your direct reports doesn't have a 1:1 scheduled",
        message_other: "{{count}} of your direct reports don't have a 1:1 scheduled",
        scheduleOneOnOne: "Schedule 1:1"
    },
    MeetingNotFound: {
        heading: "Meeting not found",
        body: "This meeting doesn't exist, or it's no longer on your calendar.",
        backToMeetings: "Back to meetings"
    },
    MeetingAdditionalContext: {
        heading: "Agenda",
        caption: "Your agent drafts this agenda on the morning of {{date}} and keeps whatever you write here.",
        editorLabel: "Anything you want to raise",
        placeholder: "Add anything you want to raise",
        empty: "Nothing added yet.",
        autoSave: {
            saving: "Saving…",
            saved: "All changes saved",
            refused: "Not saved — shorten what you wrote",
            error: "Changes not saved"
        }
    },
    MeetingAgenda: {
        heading: "Agenda",
        empty: "No agenda yet.",
        editorLabel: "Meeting agenda",
        placeholder: "Write the agenda for this meeting.",
        privateNoteLabel: "Only you · Hidden from participant",
        askAi: {
            askAi: "Ask the agent",
            input: "Ask the agent to edit this part of the agenda",
            inputPlaceholder: "Ask me to edit…",
            send: "Send",
            close: "Close"
        },
        citations: {
            pillLabel: "Source {{index}}: {{title}}",
            authorAndDate: "{{author}} • {{date}}"
        },
        autoSave: {
            saving: "Saving…",
            saved: "All changes saved",
            refused: "Not saved — shorten the agenda",
            error: "Changes not saved"
        }
    },
    MeetingRecap: {
        heading: "Recap",
        syncedFrom: "Synced from {{providers}}",
        manualEdit: "Manual edit by owner",
        notCollectedYet: "No recap yet — your note-taking apps are checked once the meeting ends.",
        states: {
            Polling: "Getting your recap",
            Complete: "No recap yet.",
            NothingFound: "Nothing was recorded for this meeting",
            ProvidersUnreachable: "Couldn't reach your note-taking apps"
        } satisfies Record<MeetingRecapState, string>,
        pollingDescription: "Workleap is looking for this meeting's transcript in your note-taking apps and keeps checking for up to an hour after the meeting's scheduled end. The recap will appear here on its own; you can leave the page.",
        nothingFoundDescription: "Workleap found no transcript of this meeting in your note-taking apps and has stopped checking.",
        providersUnreachableDescription: "Your note-taking apps didn't respond, and Workleap has stopped checking. Nothing was lost — the transcript stays in your app.",
        editWithAgent: {
            label: "Edit the recap",
            suggestions: {
                shorten: {
                    label: "Make it shorter",
                    prompt: "Make the recap shorter — keep only what was decided and what comes next."
                },
                show: {
                    label: "Show me the transcripts",
                    prompt: "Show me the transcripts."
                },
                redo: {
                    label: "Redo the recap from what is available now",
                    prompt: "Redo the recap from what's there now."
                }
            }
        }
    },
    MeetingContextPanel: {
        previousRecap: "Recap of the {{date}} meeting",
        evidenceWindow: "Activity from {{start}} to {{end}}",
        heading: "Context",
        evidence_one: "Source ({{count}})",
        evidence_other: "Sources ({{count}})"
    },
    MeetingCitationSourceTypes: {
        Performance: "Performance",
        Officevibe: "Officevibe",
        Slack: "Slack",
        Confluence: "Confluence",
        SharePoint: "SharePoint",
        Jira: "Jira",
        Goal: "Goal",
        Review: "Review",
        Recognition: "Good Vibes",
        FeedbackConversation: "Feedback",
        Meeting: "Meeting"
    },
    AskAiButton: {
        inputLabel: "What should the agent change?",
        inputPlaceholder: "What do you want to change?",
        send: "Send",
        suggestionsLabel: "Suggestions"
    },
    MeetingTemplatePicker: {
        ariaLabel: "Agenda templates",
        noTemplate: "Choose a template",
        suggestedBadge: "Suggested",
        autoMatched: "Auto-matched",
        caption: "Draft by your agent from {{name}}'s recent activity, using",
        captionWithoutCounterpart: "Draft by your agent from recent activity, using",
        prompt: 'Prepare the agenda for my upcoming meeting with the template "{{template}}"',
        manageTemplates: "Manage templates",
        editWithAgent: {
            label: "Ask for edits…",
            suggestions: {
                shorten: {
                    label: "Make it shorter",
                    prompt: "Make the agenda shorter — keep only the most important topics."
                }
            }
        }
    },
    MeetingDuration: {
        minutes_one: "{{count}} minute",
        minutes_other: "{{count}} minutes",
        hours_one: "{{count}} hour",
        hours_other: "{{count}} hours",
        hoursAndMinutes: "{{hours}} h {{minutes}} min"
    },
    MeetingSwitcher: {
        triggerAriaLabel: "Change meeting — currently {{schedule}}",
        sections: {
            upcoming: "Upcoming",
            past: "Past"
        },
        today: "Today",
        currentMeeting: "Currently open",
        loading: "Loading meetings…",
        empty: "No meetings.",
        error: "We couldn't load your meetings right now.",
        seeAllMeetings: "See all meetings"
    },
    MeetingsWidget: {
        heading: {
            today: "Today",
            upcoming: "Upcoming"
        },
        oneOnOneBadge: "1:1",
        empty: "No upcoming meetings.",
        overdueActionItems_one: "{{count}} action item overdue",
        overdueActionItems_other: "{{count}} action items overdue",
        createMeeting: {
            label: "Create a meeting",
            prompt: "Book a meeting"
        },
        notConnected: {
            heading: "No calendar connected",
            body: "Connect Outlook or Google to see your next meeting.",
            link: "Connect calendar"
        },
        expired: {
            heading: "Calendar connection expired",
            body: "Reconnect Outlook or Google to see your next meeting.",
            link: "Reconnect calendar"
        },
        error: "We couldn't load your next meeting right now.",
        liveRegion: {
            loading: "Loading…"
        }
    },
    WeeklyCalendar: {
        title: "The week ahead",
        empty: "No calendar connected — connect Outlook or Google to see your 1:1s and check-ins this week.",
        connectCalendar: "Connect my calendar",
        expired: "Your calendar connection expired — reconnect Outlook or Google to see your 1:1s and check-ins this week.",
        reconnectCalendar: "Reconnect my calendar",
        refreshPrompt: "Refresh the page to see the updated calendar.",
        refresh: "Refresh",
        error: "We couldn't load your meetings right now.",
        loading: "Loading your week…",
        previousWeek: "Previous week",
        nextWeek: "Next week",
        returnToThisWeek: "Return to this week",
        today: "Today",
        typeLabel: "Type",
        allMeetings: "All meetings",
        oneOnOnes: "1:1s",
        groupMeetings: "Group meetings",
        otherMeetings: "Everything else",
        multipleTypes: "{{typeCount}} meeting types",
        peopleLabel: "People",
        everyone: "Everyone",
        directReports: "My direct reports",
        noMeetings: "No meetings scheduled this week.",
        noFilteredMeetings: "No meetings match these filters this week.",
        showWeekends: "Show weekends",
        timeZoneInfoLabel: "Time zone",
        timeZoneInfo: "Meetings are shown in {{timeZone}}.",
        meetingLinkLabel: "Open {{subject}} at {{time}}"
    },
    OrganizeMeetingsButton: {
        label: "Manage calendar",
        flagOneOnOnes: {
            label: "Schedule missing 1:1s",
            prompt: "Help me schedule a 1:1 with each of my direct reports who doesn't have one"
        },
        bookMeeting: {
            label: "Book a meeting",
            prompt: "Book a meeting"
        },
        bookMeetingWithPerson: {
            label: "Book a meeting with {{name}}",
            prompt: "I want to book a meeting with {{name}}"
        }
    },
    ConnectCalendarLink: {
        opensInNewTab: "Opens in a new tab"
    },
    MeetingErrors: {
        retry: "Try again",
        retrying: "Retrying…",
        reassurance: "Please try again later.",
        refreshBanner: "Couldn't refresh — showing the last results."
    }
};
